import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useBranches } from "../components/SpecialOrder/useBranches";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PriceBookFilter from "../components/Product/PriceBook/Filter";
import PriceBookTable from "../components/Product/PriceBook/Table";
import PriceBookDrawer from "../components/Product/PriceBook/PriceBookDrawer";
import ExpiringPeriodBanner from "../components/Product/PriceBook/ExpiringPeriodBanner";
import ScheduledBookBanner from "../components/Product/PriceBook/ScheduledBookBanner";
import { defaultPresetGroups } from "../components/common/DateRangePicker";
import {
  API_DATE_FORMAT,
  SCHEDULED_FILTER_VALUE,
} from "../components/Product/PriceBook/constants";
import { ALL_FILTER_VALUE } from "../components/Product/shared/constants";
import { PriceBookDrawerMode } from "../components/Product/PriceBook/types";

const SEARCH_DEBOUNCE_MS = 500;

/**
 * How far back "ทั้งหมดที่มี" reaches on this screen, and the range the list opens on. The
 * picker has no unbounded state, and a pricebook list that hid everything created before this
 * month would be useless — so the widest span the endpoint will serve IS the default here.
 */
const CREATED_LOOKBACK_YEARS = 5;

export default function PriceBookPage() {
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);

  const [search, setSearch] = useState<string>("");
  const [priceBookType, setPriceBookType] = useState<string>(ALL_FILTER_VALUE);
  const [status, setStatus] = useState<string>(ALL_FILTER_VALUE);
  const [branchCompanyId, setBranchCompanyId] =
    useState<string>(ALL_FILTER_VALUE);
  const branches = useBranches(true);
  const createdMinDate = useMemo(
    () => dayjs().startOf("day").subtract(CREATED_LOOKBACK_YEARS, "year"),
    [],
  );
  const createdPresetGroups = useMemo(
    () =>
      defaultPresetGroups({
        allTimeLookbackDays: dayjs().startOf("day").diff(createdMinDate, "day"),
      }),
    [createdMinDate],
  );
  const [createdFrom, setCreatedFrom] = useState<Dayjs>(createdMinDate);
  const [createdTo, setCreatedTo] = useState<Dayjs>(() => dayjs().startOf("day"));
  const [triggerFetch, setTriggerFetch] = useState<number>(0);
  const [triggerFirstPage, setTriggerFirstPage] = useState<number>(0);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [drawerMode, setDrawerMode] = useState<PriceBookDrawerMode>("create");
  const [drawerPriceBookUuid, setDrawerPriceBookUuid] = useState<string>("");

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const handleChangeCreatedRange = (start: Dayjs, end: Dayjs) => {
    setCreatedFrom(start);
    setCreatedTo(end);
  };

  const handleOpenCreateDrawer = () => {
    setDrawerMode("create");
    setDrawerPriceBookUuid("");
    setOpenDrawer(true);
  };

  const handleOpenDuplicateDrawer = (uuid: string) => {
    setDrawerMode("duplicate");
    setDrawerPriceBookUuid(uuid);
    setOpenDrawer(true);
  };

  const handleShowScheduled = () => setStatus(SCHEDULED_FILTER_VALUE);

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setDrawerPriceBookUuid("");
  };

  // The drawer closes itself after a save; the list only has to re-read. A create or a
  // duplicate writes a pricebook the newest-first list puts on page 1, so the list is sent
  // back there too — otherwise the row it just saved stays off-screen.
  const handleRefreshList = () => {
    setTriggerFirstPage((prev) => prev + 1);
    setTriggerFetch((prev) => prev + 1);
  };

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={1}
        >
          <Stack gap={0.5}>
            <Typography variant="h6">สมุดราคา</Typography>
            <Typography variant="body2" color="text.secondary">
              ราคาต่อหน่วยหักส่วนลดสมุด ใช้ได้ในสาขาที่เปิดให้แต่ละเล่ม
            </Typography>
          </Stack>
          {canEditCatalogue && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDrawer}
            >
              เพิ่มสมุดราคา
            </Button>
          )}
        </Stack>

        <ExpiringPeriodBanner />
        <ScheduledBookBanner onShowScheduled={handleShowScheduled} />

        <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
          <PriceBookFilter
            search={search}
            setSearch={setSearch}
            priceBookType={priceBookType}
            setPriceBookType={setPriceBookType}
            status={status}
            setStatus={setStatus}
            branchCompanyId={branchCompanyId}
            setBranchCompanyId={setBranchCompanyId}
            branches={branches}
            createdFrom={createdFrom}
            createdTo={createdTo}
            onChangeCreatedRange={handleChangeCreatedRange}
            createdMinDate={createdMinDate}
            presetGroups={createdPresetGroups}
          />
        </Paper>

        <PriceBookTable
          search={debouncedSearch}
          priceBookType={priceBookType}
          status={status}
          branchCompanyId={branchCompanyId}
          createdFrom={createdFrom.format(API_DATE_FORMAT)}
          createdTo={createdTo.format(API_DATE_FORMAT)}
          triggerFetch={triggerFetch}
          triggerFirstPage={triggerFirstPage}
          onOpenDuplicateDrawer={handleOpenDuplicateDrawer}
        />
      </Box>

      <PriceBookDrawer
        open={openDrawer}
        mode={drawerMode}
        priceBookUuid={drawerPriceBookUuid}
        canSetValidity
        onClose={handleCloseDrawer}
        onSaved={handleRefreshList}
        onDeleted={handleRefreshList}
      />
    </>
  );
}
